import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useStakingData} from '@/providers/overview/useStakingData';
import {useKEIConnect} from '@kei.fi/connect';
import {usePositions} from '@/providers/overview/usePositions';
import {useAssets} from '@/providers/overview/useAssets';
import {usePriceHistory} from '@/providers/overview/usePriceHistories';
import {useChartData} from '@/providers/overview/useChartData';
import {useAccountData} from '@/providers/account/AccountProvider';
import {ETH_ADDRESS, TOKEN_ADDRESS} from '@/util/chains';
import {FullTokenInfo} from '@/api-requests/tokens';
import {useStakingDataOLD} from './useStakingDataOLD';
import {useAssetManagers} from '../invest/useAssetManagers';
import {useAssetManagerData} from './useAssetManagerData';

export enum PositionEventType {
  ACCUMULATE = 'ACCUMULATE',
  SWAP = 'SWAP',
  RECEIVE = 'RECEIVE',
  SEND = 'SEND',
  GAS = 'GAS',
}

export type PositionBalance<T = void> = {
  value: number;
  balance: string;
  timestamp: number;
  blockNumber: string;
  getValueAt(timestamp?: number): number;
} & (T extends void ? {} : {meta: T});

export type PositionEvent<T = void> = {
  decimals: number;
  type: PositionEventType;
  symbol: string;
  amount: string;
  value: number;
  timestamp: number;
  tx: string;
} & (T extends void ? {} : {meta: T});

export interface PositionAsset {
  id: string;
  symbol: string;
  name: string;
  image: string;
  balance: string;
  decimals: number;
  color: FullTokenInfo['color'];
  value: number;
}

export type Position<
  PositionMetaData = void,
  BalanceMetaData = void,
  EventsMetaData = void,
> = {
  id: string;
  old?: boolean;
  type: 'Stake' | 'Token' | 'AssetManager';
  symbol: string;
  name: string;
  decimals: number;
  value: number;
  growth: number;
  balance: string;
  color: FullTokenInfo['color'];
  image: string;
  assets: PositionAsset[];
  balances: PositionBalance<BalanceMetaData>[];
  events: PositionEvent<EventsMetaData>[];
  chartData: TimeSeriesValues[];
} & (PositionMetaData extends void ? {} : {meta: PositionMetaData});

export interface Asset extends PositionAsset {
  price: number;
  growth: number;
  chartData: TimeSeriesValues[];
}

export interface TimeSeriesValues {
  timestamp: number;
  value: number;
}

export interface ChartDataItem {
  timeSeries: TimeSeriesValues[];
  record: Record<string | number, number>;
  image: string;
  color: FullTokenInfo['color'];
  name: string;
  symbol: string;
  tick: number;

  getPrice(timestamp: number | undefined, defaultPrice: number): number;

  getPrice(timestamp?: number): number | undefined;

  getPrice(
    timestamp: number | undefined,
    defaultPrice?: number
  ): number | undefined;

  getTimestampFromLast(ticksAgo: number): number;
}

export type ChartData = Record<string, ChartDataItem>;

export interface OverviewData {
  positions: Position[];
  assets: Asset[];
  chartData: ChartData;
  isLoading?: boolean;
  error?: unknown;
}

export const OverviewContext = createContext<OverviewData>(null!);

export const OverviewProvider = ({children}: PropsWithChildren) => {
  const {signer, isInitialising} = useKEIConnect();
  const {isLoading: accountsDataIsLoading, balances} = useAccountData();

  const {
    data: assetManagers,
    isLoading: assetManagersLoading,
    error: assetManagersError,
  } = useAssetManagers();

  const priceHistoryBalances = useMemo(() => {
    const assetsToUse =
      balances?.map(balance => balance.contract_address).sort() || [];
    if (!assetsToUse.includes(TOKEN_ADDRESS)) {
      assetsToUse.push(TOKEN_ADDRESS);
    }
    if (!assetsToUse.includes(ETH_ADDRESS)) {
      assetsToUse.push(ETH_ADDRESS);
    }
    if (assetManagers) {
      assetsToUse.push(
        ...assetManagers.flatMap(assetManager => [
          assetManager.portfolio.baseAsset.asset,
          ...Object.keys(assetManager.assets),
        ])
      );
    }
    return Array.from(new Set(assetsToUse));
  }, [balances, assetManagers]);

  const {
    data: assetManagerData,
    loading: assetManagerDataLoading,
    error: assetManagerDataError,
  } = useAssetManagerData(signer?.address);

  const {
    data: stakingDataOLD,
    loading: stakingIsLoadingOLD,
    error: stakingErrorOLD,
  } = useStakingDataOLD(signer?.address);

  const {
    data: stakingData,
    loading: stakingIsLoading,
    error: stakingError,
  } = useStakingData(signer?.address);

  const {
    data: priceHistoryData = [],
    isLoading: priceHistoriesLoading,
    error: priceHistoriesError,
  } = usePriceHistory(priceHistoryBalances);

  const chartData = useChartData(priceHistoryData);

  const positions = usePositions(
    chartData,
    stakingData,
    stakingDataOLD,
    assetManagers,
    assetManagerData
  );

  const assets = useAssets(chartData, positions);

  const isLoading =
    stakingIsLoading ||
    stakingIsLoadingOLD ||
    accountsDataIsLoading ||
    isInitialising ||
    priceHistoriesLoading ||
    assetManagersLoading ||
    assetManagerDataLoading;
  const error =
    stakingError ||
    stakingErrorOLD ||
    priceHistoriesError ||
    assetManagersError ||
    assetManagerDataError;

  if (error) {
    console.error({
      stakingError,
      stakingErrorOLD,
      priceHistoriesError,
      assetManagersError,
      assetManagerDataError,
    });
  }

  return (
    <OverviewContext.Provider
      value={{
        isLoading,
        positions,
        assets,
        error,
        chartData,
      }}
    >
      {children}
    </OverviewContext.Provider>
  );
};

export const useOverview = () => {
  const overview = useContext(OverviewContext);

  if (!overview) {
    throw new Error('missing overview provider');
  }

  return overview;
};
